import React from "react";
import { useNavigate } from "react-router-dom";
import WorldsOverview from "../components/learn/maps/WorldsOverview";
import useLearnProgress from "../hooks/useLearnProgress";
import { useLearnCloudNavigate } from "../components/learn/PixleyCloudTransition";
import { LEARN } from "../components/learn/theme";
import { countingLessonByNumber } from "../data/countingLadderCatalog";
import {
  COUNTING_WORLDS, countingLessonChipState, countingWorldRoute, isCountingWorldComplete, isCountingWorldUnlocked,
} from "../utils/learnCountingProgress";

const T = LEARN.course.counting;

export default function CountingWorldsPage() {
  const navigate = useNavigate();
  const { navigateWithCloud } = useLearnCloudNavigate();
  const { countingSet, nextLesson, continueWorld, completedWorlds, countingFinished, loading } = useLearnProgress();

  const openWorld = (world) => {
    if (!world || !isCountingWorldUnlocked(world, countingSet)) return;
    navigateWithCloud(countingWorldRoute(world.id));
  };

  const worlds = COUNTING_WORLDS.map((w, i) => {
    const unlocked = isCountingWorldUnlocked(w, countingSet);
    const complete = isCountingWorldComplete(w, countingSet);
    const state = !unlocked ? "locked" : complete ? "complete" : continueWorld?.id === w.id ? "current" : "open";
    const first = countingLessonByNumber(w.lessonNumbers[0]);
    const last = countingLessonByNumber(w.lessonNumbers[w.lessonNumbers.length - 1]);
    return {
      id: w.id,
      title: w.title,
      subtitle: first && last ? `${first.title} → ${last.title}` : `${w.lessonNumbers.length} lessons`,
      art: `counting/worlds/${w.overviewArt}`,
      state,
      chips: w.lessonNumbers.map((_, idx) => ({ label: String(idx + 1), state: countingLessonChipState(w, idx, countingSet) })),
      note: `Finish ${COUNTING_WORLDS[i - 1]?.title || "the world before"} first`,
      onOpen: () => openWorld(w),
    };
  });

  const resumeWorld = continueWorld || COUNTING_WORLDS[0];

  return (
    <WorldsOverview
      eyebrow="Counting"
      title="Counting Worlds"
      accent={T.accent}
      accentDeep={T.deep}
      progress={{ done: completedWorlds, total: COUNTING_WORLDS.length, unit: "worlds" }}
      loading={loading}
      resume={{
        title: countingFinished || !nextLesson ? "Every number counted!" : nextLesson.title,
        subtitle: countingFinished || !nextLesson ? "All three worlds are open to play again." : `${resumeWorld.title} · Lesson ${nextLesson.number}`,
        cta: countingFinished || !nextLesson ? "Explore" : countingSet?.size ? "Continue" : "Start",
        art: `counting/worlds/${resumeWorld.overviewArt}`,
        onStart: () => openWorld(resumeWorld),
      }}
      worlds={worlds}
      onBack={() => navigate("/learn")}
    />
  );
}
